import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, AlertCircle, CheckCircle2, X, FileType, Image, ChevronLeft, ChevronRight, GraduationCap, BookOpen } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { createFiche } from '../lib/api';

interface FileWithPreview {
  file: File;
  preview: string;
}

interface FormState {
  sujet: string;
  matiere: string;
  niveau: string;
  creerQuiz: boolean;
  partager: boolean;
  currentStep: number;
}

const STORAGE_KEY = 'nouvelle_fiche_state';

const NouvelleFichePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadMode, setUploadMode] = useState<'images' | 'pdf' | null>(null);
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [defaultLevel, setDefaultLevel] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<FormState>(() => {
    const savedState = localStorage.getItem(STORAGE_KEY);
    return savedState ? JSON.parse(savedState) : {
      sujet: '',
      matiere: '',
      niveau: '',
      creerQuiz: false,
      partager: true,
      currentStep: 1
    };
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    return () => {
      localStorage.removeItem(STORAGE_KEY);
    };
  }, []);

  useEffect(() => {
    const fetchDefaultLevel = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('default_level')
          .eq('id', user.id)
          .single();

        if (error) throw error;

        if (data?.default_level) {
          if (!formData.niveau) {
            setFormData(prev => ({ 
              ...prev, 
              niveau: data.default_level,
              currentStep: 2 
            }));
          }
          setDefaultLevel(data.default_level);
          setSaveAsDefault(formData.niveau === data.default_level);
        }
      } catch (err) {
        console.error('Error fetching default level:', err);
      }
    };

    fetchDefaultLevel();
  }, [user, formData.niveau]);

  const getRegularMatieres = (niveau: string) => {
    const baseRegularMatieres = [
      'Allemand',
      'Anglais',
      'Arts plastiques',
      'Enseignement scientifique',
      'Espagnol',
      'Français',
      'Histoire-Géo-EMC',
      'Musique',
      'Philosophie',
      'Physique-Chimie',
      'Sciences Numériques et Technologie',
      'SVT (Sciences de la Vie et de la Terre)',
      'Technologie'
    ];

    if (!niveau || ['6ème', '5ème', '4ème', '3ème', 'Seconde'].includes(niveau)) {
      baseRegularMatieres.push('Mathématiques');
    }
    if (niveau === 'Seconde') {
      baseRegularMatieres.push('SES (Sciences Économiques et Sociales)');
    }

    return baseRegularMatieres.sort();
  };

  const getSpecialtyMatieres = (niveau: string) => {
    const baseSpecialtyMatieres = [
      'Éduc. Médias et Information (EMI)',
      'Hist. Géo, Géopolitique et Sciences Pol.',
      'Humanités, Littérature et Philosophie',
      'Langues, Littératures et Cultures Étrangères',
      'Numérique et Sciences Informatiques',
      'Sciences de l\'ingénieur'
    ];

    if (['Première', 'Terminale'].includes(niveau)) {
      baseSpecialtyMatieres.push('Mathématiques');
      baseSpecialtyMatieres.push('SES (Sciences Économiques et Sociales)');
    }

    return baseSpecialtyMatieres.sort();
  };

  const optionalMatieres = [
    'Grec',
    'Latin'
  ].sort();

  const niveaux = {
    'Collège': ['6ème', '5ème', '4ème', '3ème'],
    'Lycée': ['Seconde', 'Première', 'Terminale']
  };

  const isSubjectAvailable = (matiere: string, niveau: string): boolean => {
    if (!niveau) return true;

    if (matiere === 'Technologie' && 
        !['6ème', '5ème', '4ème', '3ème'].includes(niveau)) {
      return false;
    }

    if (niveau === '6ème' && ['Latin', 'Grec'].includes(matiere)) {
      return false;
    }

    if (matiere === 'Sciences Numériques et Technologie' && niveau !== 'Seconde') {
      return false;
    }

    if (matiere === 'SES (Sciences Économiques et Sociales)') {
      if (!['Seconde', 'Première', 'Terminale'].includes(niveau)) {
        return false;
      }
      if (['Première', 'Terminale'].includes(niveau)) {
        return getSpecialtyMatieres(niveau).includes(matiere);
      }
      return getRegularMatieres(niveau).includes(matiere);
    }

    if (matiere === 'Mathématiques') {
      if (['Première', 'Terminale'].includes(niveau)) {
        return getSpecialtyMatieres(niveau).includes(matiere);
      }
      return getRegularMatieres(niveau).includes(matiere);
    }

    if (matiere === 'Enseignement scientifique' && 
        !['Première', 'Terminale'].includes(niveau)) {
      return false;
    }

    const specialtyMatieres = [
      'Hist. Géo, Géopolitique et Sciences Pol.',
      'Éduc. Médias et Information (EMI)',
      'Humanités, Littérature et Philosophie',
      'Langues, Littératures et Cultures Étrangères',
      'Numérique et Sciences Informatiques',
      'Sciences de l\'ingénieur'
    ];
    if (specialtyMatieres.includes(matiere) && 
        !['Première', 'Terminale'].includes(niveau)) {
      return false;
    }

    if (matiere === 'Philosophie' && niveau !== 'Terminale') {
      return false;
    }

    return true;
  };

  const handleNiveauChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newNiveau = e.target.value;
    setSaveAsDefault(newNiveau === defaultLevel);
    setFormData(prev => ({ 
      ...prev, 
      niveau: newNiveau, 
      matiere: '' 
    }));
  };

  const handleSaveDefaultLevel = async () => {
    if (!user) return;

    try {
      if ((saveAsDefault && formData.niveau !== defaultLevel) || 
          (!saveAsDefault && defaultLevel === formData.niveau)) {
        const { error } = await supabase
          .from('profiles')
          .update({ 
            default_level: saveAsDefault ? formData.niveau : null 
          })
          .eq('id', user.id);

        if (error) throw error;

        setDefaultLevel(saveAsDefault ? formData.niveau : null);
      }
    } catch (err) {
      console.error('Error saving default level:', err);
      setError('Erreur lors de l\'enregistrement de la classe par défaut');
    }
  };

  const handleNextStep = async () => {
    setError(null);

    if (formData.currentStep === 1) {
      if (!formData.niveau) {
        setError('Veuillez sélectionner une classe');
        return;
      }

      try {
        await handleSaveDefaultLevel();
      } catch (err) {
        console.error('Error handling default level:', err);
      }
    } else if (formData.currentStep === 2) {
      if (!formData.matiere) {
        setError('Veuillez sélectionner une matière');
        return;
      }
    }

    setFormData(prev => ({ ...prev, currentStep: prev.currentStep + 1 }));
  };

  const handlePrevStep = () => {
    setFormData(prev => ({ ...prev, currentStep: prev.currentStep - 1 }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      setError('Vous devez être connecté pour créer une fiche');
      return;
    }

    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        sujet: formData.sujet,
        matiere: formData.matiere,
        niveau: formData.niveau,
        creerQuiz: formData.creerQuiz.toString(),
        partager: formData.partager.toString(),
        user_id: user.id,
        public: formData.partager.toString()
      });

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const reader = new FileReader();
        await new Promise((resolve, reject) => {
          reader.onload = resolve;
          reader.onerror = reject;
          reader.readAsDataURL(file.file);
        });
        params.append(`file${i + 1}`, reader.result as string);
      }

      const data = await createFiche(params);
      
      setShowSuccessModal(true);
      setTimeout(() => {
        navigate('/mes-fiches');
      }, 3000);

    } catch (err) {
      console.error('Error creating fiche:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      addFiles(newFiles);
    }
  };

  const validateFiles = (filesToValidate: File[]): File[] => {
    const validFiles: File[] = [];
    const errors: string[] = [];

    const hasPDF = filesToValidate.some(file => file.type === 'application/pdf');
    const hasImages = filesToValidate.some(file => file.type.startsWith('image/'));

    if (hasPDF && hasImages) {
      errors.push('Vous ne pouvez pas mélanger des PDFs et des images');
      return [];
    }

    filesToValidate.forEach(file => {
      const isImage = file.type.startsWith('image/');
      const isPDF = file.type === 'application/pdf';
      const isValidType = isImage || isPDF;
      const isValidSize = file.size <= 10 * 1024 * 1024;

      if (!isValidType) {
        errors.push(`Le fichier "${file.name}" n'est pas d'un type supporté`);
      } else if (!isValidSize) {
        errors.push(`Le fichier "${file.name}" est trop volumineux`);
      } else if (isPDF && files.length + validFiles.length >= 1) {
        errors.push('Vous ne pouvez ajouter qu\'un seul fichier PDF');
      } else if (isImage && files.length + validFiles.length >= 5) {
        errors.push('Vous ne pouvez pas ajouter plus de 5 images');
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      setError(errors.join('\n'));
    }

    return validFiles;
  };

  const addFiles = (newFiles: File[]) => {
    const validFiles = validateFiles(newFiles);

    if (validFiles.length > 0) {
      const isPDF = validFiles[0].type === 'application/pdf';
      setUploadMode(isPDF ? 'pdf' : 'images');

      const filesWithPreviews = validFiles.map(file => ({
        file,
        preview: URL.createObjectURL(file)
      }));

      setFiles(prev => [...prev, ...filesWithPreviews]);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => {
      const newFiles = [...prev];
      if (newFiles[index].preview.startsWith('blob:')) {
        URL.revokeObjectURL(newFiles[index].preview);
      }
      newFiles.splice(index, 1);
      if (newFiles.length === 0) {
        setUploadMode(null);
      }
      return newFiles;
    });
  };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      addFiles(droppedFiles);
    }
  }, []);

  if (showSuccessModal) {
    return (
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="flex items-center justify-center mb-4">
            <CheckCircle2 className="h-12 w-12 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-4">
            Demande envoyée avec succès
          </h2>
          <p className="text-gray-600 text-center mb-6">
            Votre fiche est en cours de création. Vous allez être redirigé vers vos fiches.
          </p>
        </div>
      </div>
    );
  }

  const availableRegularMatieres = getRegularMatieres(formData.niveau).filter(matiere => 
    isSubjectAvailable(matiere, formData.niveau)
  );
  const availableSpecialtyMatieres = getSpecialtyMatieres(formData.niveau).filter(matiere => 
    isSubjectAvailable(matiere, formData.niveau)
  );
  const availableOptionalMatieres = optionalMatieres.filter(matiere => 
    isSubjectAvailable(matiere, formData.niveau)
  );

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Créer une nouvelle fiche de révision</h1>

      {(error || validationError) && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-start">
          <AlertCircle className="h-5 w-5 text-red-400 mr-2 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-red-600 whitespace-pre-line">
            {error || validationError}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 bg-white shadow-lg sm:rounded-lg p-5">
        {formData.currentStep === 1 ? (
          <>
            <div className="space-y-4">
              <div>
                <label htmlFor="niveau" className="block text-sm font-medium text-gray-700 mb-1">
                  Classe *
                </label>
                <select
                  id="niveau"
                  required
                  className="block w-full px-3 py-2 rounded-lg border-2 border-gray-300 shadow-sm focus:border-secondary-500 focus:ring-2 focus:ring-secondary-200 sm:text-sm transition-colors bg-white"
                  value={formData.niveau}
                  onChange={handleNiveauChange}
                  disabled={isSubmitting}
                >
                  <option value="">Classe</option>
                  <optgroup label="Collège">
                    {niveaux.Collège.map((niveau) => (
                      <option key={niveau} value={niveau}>{niveau}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Lycée">
                    {niveaux.Lycée.map((niveau) => (
                      <option key={niveau} value={niveau}>{niveau}</option>
                    ))}
                  </optgroup>
                </select>
              </div>

              {formData.niveau && (
                <div className="flex items-center">
                  <input
                    id="saveAsDefault"
                    type="checkbox"
                    className="h-4 w-4 text-secondary-600 focus:ring-secondary-500 border-gray-300 rounded"
                    checked={saveAsDefault}
                    onChange={(e) => setSaveAsDefault(e.target.checked)}
                  />
                  <label htmlFor="saveAsDefault" className="ml-2 block text-sm text-gray-900">
                    Définir comme classe par défaut
                  </label>
                </div>
              )}
            </div>

            <div className="flex justify-between pt-4">
              <button
                type="button"
                onClick={() => navigate('/mes-fiches')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Annuler
              </button>
              <button
                type="button"
                onClick={handleNextStep}
                disabled={!formData.niveau}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-secondary-600 hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
                <ChevronRight className="h-4 w-4 ml-2" />
              </button>
            </div>
          </>
        ) : formData.currentStep === 2 ? (
          <>
            <div className="mb-6 bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <GraduationCap className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-900">{formData.niveau}</span>
                </div>
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className="inline-flex items-center text-sm font-medium text-secondary-600 hover:text-secondary-700"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Modifier
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="matiere" className="block text-sm font-medium text-gray-700 mb-1">
                Matière *
              </label>
              <select
                id="matiere"
                required
                className="block w-full px-3 py-2 rounded-lg border-2 border-gray-300 shadow-sm focus:border-secondary-500 focus:ring-2 focus:ring-secondary-200 sm:text-sm transition-colors bg-white"
                value={formData.matiere}
                onChange={(e) => setFormData({ ...formData, matiere: e.target.value })}
                disabled={isSubmitting}
              >
                <option value="">Matière</option>
                {availableRegularMatieres.length > 0 && (
                  <optgroup label="Matières générales">
                    {availableRegularMatieres.map((matiere) => (
                      <option key={matiere} value={matiere}>{matiere}</option>
                    ))}
                  </optgroup>
                )}
                {availableSpecialtyMatieres.length > 0 && (
                  <optgroup label="Spécialités">
                    {availableSpecialtyMatieres.map((matiere) => (
                      <option key={matiere} value={matiere}>{matiere}</option>
                    ))}
                  </optgroup>
                )}
                {availableOptionalMatieres.length > 0 && (
                  <optgroup label="Options">
                    {availableOptionalMatieres.map((matiere) => (
                      <option key={matiere} value={matiere}>{matiere}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>

            <div className="flex justify-between pt-4">
              <button
                type="button"
                onClick={handlePrevStep}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Retour
              </button>
              <button
                type="button"
                onClick={handleNextStep}
                disabled={!formData.matiere}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-secondary-600 hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
                <ChevronRight className="h-4 w-4 ml-2" />
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-6 bg-gray-50 rounded-lg p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center space-x-6">
                  <div className="flex items-center">
                    <GraduationCap className="h-5 w-5 text-gray-400 mr-2" />
                    <span className="text-sm font-medium text-gray-900">{formData.niveau}</span>
                  </div>
                  <div className="flex items-center">
                    <BookOpen className="h-5 w-5 text-gray-400 mr-2" />
                    <span className="text-sm font-medium text-gray-900">{formData.matiere}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className="inline-flex items-center text-sm font-medium text-secondary-600 hover:text-secondary-700"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Modifier
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="sujet" className="block text-sm font-medium text-gray-700 mb-1">
                Sujet *
              </label>
              <textarea
                id="sujet"
                required
                rows={4}
                className="block w-full px-3 py-2 rounded-lg border-2 border-gray-300 shadow-sm focus:border-secondary-500 focus:ring-2 focus:ring-secondary-200 sm:text-sm transition-colors bg-white resize-y"
                value={formData.sujet}
                onChange={(e) => setFormData({ ...formData, sujet: e.target.value })}
                placeholder="Décrivez le sujet que vous souhaitez étudier. Plus votre description sera précise, meilleure sera la fiche générée."
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Documents (optionnel)
              </label>
              <div className="mt-1 mb-2 flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => setUploadMode(uploadMode === 'images' ? null : 'images')}
                  disabled={isSubmitting}
                  className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${
                    uploadMode === 'images'
                      ? 'border-secondary-500 bg-secondary-50 text-secondary-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Image className="h-4 w-4 mr-2" />
                  Images (max 5)
                </button>
                <button
                  type="button"
                  onClick={() => setUploadMode(uploadMode === 'pdf' ? null : 'pdf')}
                  disabled={isSubmitting}
                  className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${
                    uploadMode === 'pdf'
                      ? 'border-secondary-500 bg-secondary-50 text-secondary-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <FileType className="h-4 w-4 mr-2" />
                  PDF
                </button>
              </div>
              
              {uploadMode && (
                <div
                  className={`mt-1 flex justify-center px-6 pt-4 pb-5 border-2 ${
                    isDragging ? 'border-secondary-500 bg-secondary-50' : 'border-gray-300 border-dashed'
                  } rounded-md transition-colors duration-200`}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="space-y-1 text-center">
                    <Upload className={`mx-auto h-10 w-10 ${isDragging ? 'text-secondary-500' : 'text-gray-400'}`} />
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-secondary-600 hover:text-secondary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-secondary-500"
                      >
                        <span>Importer {uploadMode === 'images' ? 'des images' : 'un PDF'}</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          onChange={handleFileChange}
                          accept={uploadMode === 'images' ? "image/jpeg,image/png,image/gif,image/webp" : "application/pdf"}
                          multiple={uploadMode === 'images'}
                          disabled={isSubmitting || (uploadMode === 'images' && files.length >= 5) || (uploadMode === 'pdf' && files.length >= 1)}
                        />
                      </label>
                      <p className="pl-1">ou glisser-déposer</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      {uploadMode === 'images' 
                        ? 'JPG, PNG, GIF ou WebP jusqu\'à 10MB (max 5 images)'
                        : 'PDF jusqu\'à 10MB'}
                    </p>
                  </div>
                </div>
              )}

              {files.length > 0 && (
                <div className="mt-3 space-y-2">
                  {files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                      <div className="flex items-center">
                        {file.file.type.startsWith('image/') ? (
                          <img
                            src={file.preview}
                            alt={`Preview ${index + 1}`}
                            className="h-8 w-8 object-cover rounded"
                          />
                        ) : (
                          <div className="h-8 w-8 flex items-center justify-center bg-gray-200 rounded">
                            <FileType className="h-5 w-5 text-gray-500" />
                          </div>
                        )}
                        <span className="ml-2 text-sm text-gray-700 truncate max-w-xs">
                          {file.file.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-gray-400 hover:text-gray-500"
                        disabled={isSubmitting}
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center">
              <input
                id="quiz"
                type="checkbox"
                className="h-4 w-4 text-secondary-600 focus:ring-secondary-500 border-gray-300 rounded"
                checked={formData.creerQuiz}
                onChange={(e) => setFormData({ ...formData, creerQuiz: e.target.checked })}
                disabled={isSubmitting}
              />
              <label htmlFor="quiz" className="ml-2 block text-sm text-gray-900">
                Je souhaite aussi faire un quiz
              </label>
            </div>

            <div className="flex justify-between pt-4">
              <button
                type="button"
                onClick={handlePrevStep}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                disabled={isSubmitting}
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Retour
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !formData.sujet}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-secondary-600 hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Envoi en cours...' : 'Créer'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default NouvelleFichePage;